/**
 * Surfaces the operator-approval queue for pairs in semi_auto mode.
 *
 * Polls /api/pairs/:slug/suggestions?status=pending every 10s. When
 * one or more rows is pending, render an animated banner with Haiku's
 * reasoning + Approve / Reject buttons. Approving dispatches the same
 * action full_auto would have run; rejecting just logs the choice.
 *
 * Banner auto-dismisses on success. Errors surface inline rather than
 * via toast so the operator stays focused on the pair's terminal.
 */

import { useCallback, useEffect, useState, type ReactNode } from "react";
import { motion, AnimatePresence } from "motion/react";
import {
  approveSuggestion,
  listSuggestions,
  rejectSuggestion,
  type PairSuggestion,
} from "@/lib/api/pair-suggestions";
import { Brain, Check, X } from "@/components/ui/icons";
import { cn } from "@/lib/utils";

interface PairSuggestionBannerProps {
  /** Pair slug. The banner only fetches when this is non-null. */
  pairSlug: string | null;
  /** Polling interval in ms. Defaults to 10s. */
  pollMs?: number;
  className?: string;
}

const ACTION_LABEL: Record<PairSuggestion["action"], string> = {
  open_trade: "Open hedge trade",
  close_pair: "Close pair",
};

export function PairSuggestionBanner({
  pairSlug,
  pollMs = 10_000,
  className,
}: PairSuggestionBannerProps): ReactNode {
  const [pending, setPending] = useState<PairSuggestion[]>([]);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!pairSlug) return;
    try {
      const rows = await listSuggestions(pairSlug, "pending");
      setPending(rows);
    } catch (e) {
      // Silent on poll errors — the banner is a non-critical surface.
      console.warn("[PairSuggestionBanner] poll failed", e);
    }
  }, [pairSlug]);

  useEffect(() => {
    if (!pairSlug) return;
    void refresh();
    const id = window.setInterval(() => void refresh(), pollMs);
    return () => window.clearInterval(id);
  }, [pairSlug, pollMs, refresh]);

  const onApprove = useCallback(
    async (s: PairSuggestion) => {
      setBusyId(s.id);
      setError(null);
      try {
        await approveSuggestion(s.id);
        await refresh();
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setBusyId(null);
      }
    },
    [refresh],
  );

  const onReject = useCallback(
    async (s: PairSuggestion) => {
      setBusyId(s.id);
      setError(null);
      try {
        await rejectSuggestion(s.id);
        await refresh();
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setBusyId(null);
      }
    },
    [refresh],
  );

  return (
    <AnimatePresence initial={false}>
      {pending.length > 0 && (
        <motion.div
          key="suggestions"
          initial={{ opacity: 0, y: -8, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -8, scale: 0.98 }}
          transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
          className={cn(
            "rounded-2xl border border-accent/30 bg-accent/5 p-4 ring-1 ring-accent/20",
            className,
          )}
        >
          <div className="mb-3 flex items-center gap-2">
            <Brain aria-hidden="true" className="h-4 w-4 text-accent" />
            <h3 className="font-sans text-xs font-semibold text-foreground">
              <span className="text-accent">AI Suggestion</span>
              {pending.length > 1 && (
                <span className="ml-1.5 rounded-full bg-accent/20 px-1.5 py-px font-mono text-[10px] tabular-nums text-accent">
                  {pending.length}
                </span>
              )}
            </h3>
          </div>

          <div className="flex flex-col gap-2">
            {pending.map((s) => (
              <SuggestionRow
                key={s.id}
                suggestion={s}
                busy={busyId === s.id}
                onApprove={() => onApprove(s)}
                onReject={() => onReject(s)}
              />
            ))}
          </div>

          {error && (
            <p aria-live="polite" className="mt-3 text-[11px] text-destructive">{error}</p>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

interface SuggestionRowProps {
  suggestion: PairSuggestion;
  busy: boolean;
  onApprove: () => void;
  onReject: () => void;
}

function SuggestionRow({
  suggestion,
  busy,
  onApprove,
  onReject,
}: SuggestionRowProps): ReactNode {
  return (
    <motion.div
      layout
      className="flex flex-col gap-2 rounded-xl bg-background/60 p-3 ring-1 ring-border/40 sm:flex-row sm:items-center sm:justify-between"
    >
      <div className="min-w-0 flex-1">
        <p className="text-[11px] font-medium uppercase tracking-wide text-foreground">
          {ACTION_LABEL[suggestion.action]}
        </p>
        {suggestion.reasoning && (
          <p className="mt-1 text-[11px] leading-snug text-muted-foreground">
            {suggestion.reasoning}
          </p>
        )}
      </div>
      <div className="flex w-full items-center justify-end gap-1.5 sm:w-auto sm:shrink-0">
        <button
          type="button"
          onClick={onApprove}
          disabled={busy}
          aria-busy={busy}
          style={{ touchAction: "manipulation" }}
          className={cn(
            "inline-flex flex-1 items-center justify-center gap-1 rounded-md bg-accent px-2.5 py-1 text-[11px] font-medium text-white transition-colors hover:bg-accent/90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent/60 sm:flex-none",
            busy && "opacity-60",
          )}
        >
          <Check aria-hidden="true" className="h-3 w-3" />
          Approve
        </button>
        <button
          type="button"
          onClick={onReject}
          disabled={busy}
          aria-busy={busy}
          style={{ touchAction: "manipulation" }}
          className={cn(
            "inline-flex flex-1 items-center justify-center gap-1 rounded-md border border-border/60 bg-card px-2.5 py-1 text-[11px] font-medium text-muted-foreground transition-colors hover:border-destructive/40 hover:text-destructive focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring sm:flex-none",
            busy && "opacity-60",
          )}
        >
          <X aria-hidden="true" className="h-3 w-3" />
          Reject
        </button>
      </div>
    </motion.div>
  );
}
